import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { TriStateCheckboxComponent } from '~/_shared/baseComponents/checkbox';
import {
  PermanentConfirmStrategy, useConfirmationModal,
} from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { CONFIG } from '~/_shared/constants/config';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import {
  BoundaryTerritoryListingExportLimit, BoundaryTerritoryListingLimit,
} from '~/boundary/constants';
import { useLoadMoreListing } from '~/boundary/hooks/useLoadMoreListing';
import { isBoundaryTerritoryHidden } from '~/boundary/settings/boundarySettings.helpers';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { ActiveBoundaryComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundary.component';
import { ActiveBoundaryFillInfoContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryFillInfo.container';
import { BoundaryItemContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryItem.container';
import { BoundaryRowLoadMoreComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryRowLoadMore.component';
import { BoundaryTerritoryContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryTerritory.container';
import { useOptimizedTerritoriesFeatureBlocker } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/useWmsFeatureBlocker';
import { isBTGAiGenerated } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryItem.helpers';
import { useBoundaryEdit } from '~/sidebar/sidebarApps/mapTools/boundary/edit/useBoundaryEdit';
import { ActiveBoundaryExtraFillsContainer } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/activeBoundaryExtraFills.container';
import { useActiveFillIdSelector } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFillSelectors';
import { patchActiveFillIntoBTG } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/extraFills.utils';
import { isBoundaryTerritoryEmpty } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import {
  useBoundaryGroupPermissions,
  useBoundaryGroupsSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { displayNameComparer } from '~/store/boundaryItems/boundaryItems.helpers';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { useBoundaryTerritoryAssignments } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import {
  boundaryTerritoryGroupsRemoveRequest, boundaryTerritoryGroupsUpdateRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  useBoundaryTerritoryGroupsSelector, useBoundaryTerritoryGroupWithActiveExtraFillSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  boundarySelectEditBoundaryTerritoryOpenModal, boundarySelectEditCloseModal,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import type { BoundarySelectState } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { convertBoundariesShowNameModal } from '~/store/frontendState/mapTools/boundary/convertBoundaries/convertBoundaries.actionCreators';
import { mapSettingsBoundariesSetPrimaryBoundaryGroupId } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.selectors';
import { setBoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import { useBoundaryFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import { openModal } from '~/store/modal/modal.actionCreators';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import {
  boundaryOptionsHeaderStyle, boundaryOptionStyle, checkboxStyle,
} from './styles';

type ActiveBoundaryContainerProps = {
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  legendMode?: boolean;
  isSubheaderHidden?: boolean;
};

export const ActiveBoundaryContainer = ({
  boundaryTerritoryGroup,
  legendMode,
  isSubheaderHidden,
}: ActiveBoundaryContainerProps) => {
  const btgInfo = useBoundaryTerritoryGroupWithActiveExtraFillSelector(boundaryTerritoryGroup.boundaryTerritoryGroupId);
  const sidebarBTGHighlightedId = useSelector(state => state.frontendState.mapTools.boundary.boundarySidebarHighlight.highlightedBoundaryTerritoryGroupId);
  const boundaryGroups = useBoundaryGroupsSelector();
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const dispatch = useDispatch();
  const { openModal: openBoundaryCustomizeMetricsModal } = useModal(ModalType.BoundaryCustomizeMetrics);
  const { openModal: openBoundaryCustomizeFillModal } = useModal(ModalType.BoundaryCustomizeFill);
  const { openModal: openCloneBoundaryTerritoryGroupModal } = useModal(ModalType.CloneBoundaryTerritoryGroup);
  const boundaryFilters = useBoundaryFilters();
  const boundaries = useMapBoundariesSelector();
  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();
  const primaryBoundaryGroupId = useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isBoundaryExportLocationsAllowed = useIsDataExportAllowed(MapTool.Boundary);
  const { startBoundaryEdit } = useBoundaryEdit();
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();
  const { openModal: openExportBoundaryTerritoryGroupDataModal } = useModal(ModalType.ExportBoundaryTerritoryGroupData);
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();
  const { openModal: openBoundaryTerritoryEditSubmitModal } = useModal(ModalType.BoundaryTerritoryEditSubmit);
  const [t] = useTranslation();
  const {
    boundaryIsOptimizedTerritoriesTrial,
    blockWhenOptimizedTerritoriesTrial,
  } = useOptimizedTerritoriesFeatureBlocker(boundaryTerritoryGroup.boundaryGroupId);

  const initialRowLimit = legendMode ? BoundaryTerritoryListingExportLimit : BoundaryTerritoryListingLimit;
  const {
    limit: shownBoundaryKeySettingCountLimit,
    increaseLimit: increaseShownBoundaryKeySettingCountLimit,
  } = useLoadMoreListing(initialRowLimit);
  const {
    limit: shownBoundaryTerritoryKeySettingCountLimit,
    increaseLimit: increaseShownBoundaryTerritoryKeySettingCountLimit,
  } = useLoadMoreListing(initialRowLimit);

  const btgWithActiveFill = btgInfo ? patchActiveFillIntoBTG(btgInfo) : boundaryTerritoryGroup;
  const isExtraFillActive = !!btgInfo?.activeFillConfig;

  const displayName = getBoundaryTerritoryGroupName(boundaryTerritoryGroup);
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryTerritoryGroup.boundaryGroupId);
  const { boundaryIsCustom } = boundaryPermissions;

  const filter = boundaryFilters.get(boundaryTerritoryGroup.boundaryGroupId);

  const removeBoundaryTerritoryGroup = useCallback((boundaryTerritoryGroupId: number, isCustom: boolean) => {
    const entity = isCustom ? t('territory group') : t('boundary');

    openConfirmationModal({
      title: t('Remove {{entity}} from map', { entity }),
      confirmCaption: t('Proceed'),
      text: t('You are about to remove a {{entity}} from the map. Do you want to proceed?',
        { entity }),
      permanentConfirmSettings: {
        id: 'remove-boundary-territory-group-from-map',
        strategy: PermanentConfirmStrategy.Session,
      },
      isConfirmButtonDestructive: true,
      onCancel: () => {
        closeConfirmationModal();
      },
      onConfirm: () => {
        dispatch(boundaryTerritoryGroupsRemoveRequest(boundaryTerritoryGroupId));
        closeConfirmationModal();
      },
    });
  }, [closeConfirmationModal, dispatch, openConfirmationModal, t]);

  const convertTerritoriesToBoundaries = useCallback((boundaryTerritoryGroupId: number) => {
    dispatch(convertBoundariesShowNameModal(boundaryTerritoryGroupId));
  }, [dispatch]);

  const activeFillId = useActiveFillIdSelector(boundaryTerritoryGroup.boundaryTerritoryGroupId) ?? undefined;

  const onToggleShowAll = useCallback(() => {
    dispatch(setBoundaryFilter(boundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaryTerritories: [],
      filteredBoundaries: [],
      showAll: filter.showAll === TriStateRange.Full
        ? TriStateRange.None
        : TriStateRange.Full,
    }, activeFillId));
  }, [boundaryTerritoryGroup.boundaryGroupId, dispatch, filter, activeFillId]);

  const onToggleFadeWhenInactive = useMemo(() => boundaryTerritoryGroups.length > 1 ? () => {
    dispatch(boundaryTerritoryGroupsUpdateRequest({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        fadeWhenInactive: !boundaryTerritoryGroup.settings.fadeWhenInactive,
      },
    }, { fetchBoundaryDetails: false }));
  } : undefined, [boundaryTerritoryGroups.length, dispatch, boundaryTerritoryGroup]);

  const onToggleHideLabels = useCallback(() => {
    dispatch(boundaryTerritoryGroupsUpdateRequest({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        hideLabels: !boundaryTerritoryGroup.settings.hideLabels,
      },
    }, { fetchBoundaryDetails: false }));
  }, [dispatch, boundaryTerritoryGroup]);

  const onToggleHideAreasWithoutData = useCallback(() => {
    dispatch(boundaryTerritoryGroupsUpdateRequest({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        hideWithoutData: !boundaryTerritoryGroup.settings.hideWithoutData,
      },
    }, { fetchBoundaryDetails: false }));
  }, [dispatch, boundaryTerritoryGroup]);

  const onToggleIgnoreFilters = useCallback(() => {
    dispatch(boundaryTerritoryGroupsUpdateRequest({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        ignoreFilters: !boundaryTerritoryGroup.settings.ignoreFilters,
      },
    }));
  }, [dispatch, boundaryTerritoryGroup]);

  const setCombineBoundariesIntoTerritories = useCallback((boundariesAsTerritories: boolean) => {
    dispatch(boundaryTerritoryGroupsUpdateRequest({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundariesAsTerritories,
      },
    }));
  }, [boundaryTerritoryGroup, dispatch]);

  const onClone = useCallback(() => openCloneBoundaryTerritoryGroupModal({
    boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
  }), [openCloneBoundaryTerritoryGroupModal, boundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const displayBoundaryTerritories = !boundaryIsCustom || boundaryTerritoryGroup.settings.boundaryTerritories.length > 0;

  const groupBoundaries = boundaries.get(boundaryTerritoryGroup.boundaryGroupId);
  const boundaryGroupArray = useMemo(() => {
    return Array.from(groupBoundaries?.values() ?? []);
  }, [groupBoundaries]);
  const boundaryTerritoryArray = useMemo(() => {
    if (btgWithActiveFill) {
      return btgWithActiveFill.settings.boundaryTerritories.filter(boundaryTerritory => {
        const isEmpty = isBoundaryTerritoryEmpty(boundaryTerritoryAssignments,
          btgWithActiveFill.boundaryTerritoryGroupId,
          boundaryTerritory);
        const isHidden = isBoundaryTerritoryHidden(boundaryTerritory,
          isEmpty,
          btgWithActiveFill.settings.boundaryTerritoryType);

        return !isHidden;
      });
    }

    return [];
  }, [boundaryTerritoryAssignments, btgWithActiveFill]);

  const handleSettingsClick = useCallback(() => {
    dispatch(openModal(ModalType.BoundarySettings, {
      boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
    }));
  }, [dispatch, boundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const handleToggleBoundariesAsTerritories = useCallback(() =>
    setCombineBoundariesIntoTerritories(!boundaryTerritoryGroup.settings.boundariesAsTerritories),
  [setCombineBoundariesIntoTerritories, boundaryTerritoryGroup.settings.boundariesAsTerritories],
  );

  const handleRemove = useCallback(() => {
    removeBoundaryTerritoryGroup(boundaryTerritoryGroup.boundaryTerritoryGroupId, boundaryIsCustom);
  }, [removeBoundaryTerritoryGroup, boundaryTerritoryGroup.boundaryTerritoryGroupId, boundaryIsCustom]);

  const handleCustomizeFillClick = useMemo(() => blockWhenOptimizedTerritoriesTrial(() => {
    openBoundaryCustomizeFillModal({
      boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
    });
  }),
  [blockWhenOptimizedTerritoriesTrial, openBoundaryCustomizeFillModal,
    boundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const handleCustomizeMetricsClick = useCallback(() => {
    openBoundaryCustomizeMetricsModal({
      boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
      isBoundaryGroupCustom: boundaryIsCustom,
    });
  }, [openBoundaryCustomizeMetricsModal, boundaryTerritoryGroup.boundaryTerritoryGroupId, boundaryIsCustom]);

  const handleStartEditClick = useMemo(() => {
    if (btgInfo?.activeFillConfig) {
      return undefined;
    }

    if (boundaryIsCustom && !boundaryPermissions.boundaryIsEditable) {
      return undefined;
    }

    return blockWhenOptimizedTerritoriesTrial(() => {
      if (boundaryIsCustom) {
        startBoundaryEdit(boundaryTerritoryGroup.boundaryTerritoryGroupId);
      }
      else {
        dispatch(boundarySelectEditBoundaryTerritoryOpenModal({
          selectBoundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
          boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
          onClose: () => {
            dispatch(boundarySelectEditCloseModal());
          },
          onSubmit: (selectState: BoundarySelectState) => {
            if (!selectState.selectedBoundaryIds?.size) {
              return;
            }

            openBoundaryTerritoryEditSubmitModal({
              boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
              boundaryIds: Array.from(selectState.selectedBoundaryIds.keys()),
            });
          },
        }));
      }
    });
  },
  [btgInfo?.activeFillConfig, boundaryIsCustom, boundaryPermissions.boundaryIsEditable, blockWhenOptimizedTerritoriesTrial, startBoundaryEdit,
    boundaryTerritoryGroup.boundaryTerritoryGroupId, boundaryTerritoryGroup.boundaryGroupId, dispatch, openBoundaryTerritoryEditSubmitModal]);

  const handleToggleIsPrimary = useCallback(() => {
    dispatch(mapSettingsBoundariesSetPrimaryBoundaryGroupId(boundaryTerritoryGroup.boundaryGroupId));
  }, [dispatch, boundaryTerritoryGroup.boundaryGroupId]);

  const handleConvert = useCallback(() =>
    convertTerritoriesToBoundaries(boundaryTerritoryGroup.boundaryTerritoryGroupId),
  [convertTerritoriesToBoundaries, boundaryTerritoryGroup.boundaryTerritoryGroupId],
  );

  const handleExportClick = useCallback(() =>
    openExportBoundaryTerritoryGroupDataModal({
      selectedBoundaryTerritoryGroupIds: [boundaryTerritoryGroup.boundaryTerritoryGroupId],
      showSelectBoundaryTerritoryGroupsSection: false,
    }),
  [openExportBoundaryTerritoryGroupDataModal, boundaryTerritoryGroup.boundaryTerritoryGroupId],
  );

  const containsLegend = boundaryGroupArray.length || boundaryTerritoryArray.length;

  if (!btgInfo || (legendMode && !containsLegend)) {
    return null;
  }

  const renderBuckets = () => {
    return (
      <>
        {!legendMode && (
          <div css={boundaryOptionsHeaderStyle}>
            <div css={boundaryOptionStyle}>
              <TriStateCheckboxComponent
                checkmark={filter.showAll}
                checkedSetter={onToggleShowAll}
                text={boundaryIsCustom ? t('Show All Territories') : t('Show All Boundaries')}
                textStyle={checkboxStyle}
              />
            </div>
          </div>
        )}

        {displayBoundaryTerritories && (
          <>
            {boundaryTerritoryArray.map(
              (boundaryTerritory, index) => {
                return (index < shownBoundaryTerritoryKeySettingCountLimit) && (
                  <BoundaryTerritoryContainer
                    key={boundaryTerritory.boundaryTerritoryId}
                    boundaryTerritory={boundaryTerritory}
                    boundaryTerritoryGroup={btgWithActiveFill}
                  />
                );
              },
            )}

            {(boundaryTerritoryArray.length > shownBoundaryTerritoryKeySettingCountLimit) && (
              <BoundaryRowLoadMoreComponent
                onButtonClick={increaseShownBoundaryTerritoryKeySettingCountLimit}
              />
            )}
          </>
        )}
      </>
    );
  };

  return (
    <ActiveBoundaryComponent
      isHighlighted={boundaryTerritoryGroup.boundaryGroupId === sidebarBTGHighlightedId}
      legendMode={legendMode}
      name={displayName}
      onSettingsClick={handleSettingsClick}
      isAiGenerated={isBTGAiGenerated(boundaryTerritoryGroup, boundaryGroups)}
      boundaryPermissions={boundaryPermissions}
      boundaryIsOptimizedTerritoriesTrial={boundaryIsOptimizedTerritoriesTrial}
      boundaryGroupId={boundaryTerritoryGroup.boundaryGroupId}
      boundaryTerritoryType={boundaryTerritoryGroup.settings.boundaryTerritoryType}
      fadeWhenInactive={boundaryTerritoryGroup.settings.fadeWhenInactive}
      isPrimary={boundaryTerritoryGroup.boundaryGroupId === primaryBoundaryGroupId}
      hideLabels={boundaryTerritoryGroup.settings.hideLabels}
      hideWithoutData={boundaryTerritoryGroup.settings.hideWithoutData}
      ignoreFilters={boundaryTerritoryGroup.settings.ignoreFilters}
      isSubheaderHidden={isSubheaderHidden}
      showToolbar={!isEditModeDisabled}
      boundariesAsTerritories={boundaryTerritoryGroup.settings.boundariesAsTerritories}
      onToggleBoundariesAsTerritories={
        (!isEditModeDisabled && !boundaryIsCustom) ? handleToggleBoundariesAsTerritories : undefined
      }
      onRemove={handleRemove}
      onCustomizeFillClick={handleCustomizeFillClick}
      onCustomizeMetricsClick={handleCustomizeMetricsClick}
      onStartEditClick={btgInfo.activeFillConfig ? undefined : handleStartEditClick}
      onToggleIsPrimary={handleToggleIsPrimary}
      onConvert={boundaryTerritoryGroup.settings.boundaryTerritories.length && !isExtraFillActive ?
        handleConvert : undefined}
      onClone={!isEditModeDisabled && boundaryIsCustom ? onClone : undefined}
      onToggleFadeWhenInactive={onToggleFadeWhenInactive}
      onToggleHideLabels={onToggleHideLabels}
      onToggleHideWithoutData={onToggleHideAreasWithoutData}
      onToggleIgnoreFilters={isEditModeDisabled ? undefined : onToggleIgnoreFilters}
      onExportClick={isBoundaryExportLocationsAllowed ? handleExportClick : undefined}
    >
      <ActiveBoundaryFillInfoContainer
        boundaryTerritoryType={boundaryTerritoryGroup.settings.boundaryTerritoryType}
        boundaryTerritoryGroupId={boundaryTerritoryGroup.boundaryTerritoryGroupId}
      />

      {!btgInfo.activeFillConfig && renderBuckets()}

      {CONFIG.ENABLE_ALT_BOUNDARY_FILLS && (
        <ActiveBoundaryExtraFillsContainer
          boundaryTerritoryGroupId={boundaryTerritoryGroup.boundaryTerritoryGroupId}
        >
          {renderBuckets()}
        </ActiveBoundaryExtraFillsContainer>
      )}

      {(!displayBoundaryTerritories && groupBoundaries) && boundaryGroupArray
        .sort(displayNameComparer)
        .map((boundary, index) => {
          if (index < shownBoundaryKeySettingCountLimit) {
            return (
              <BoundaryItemContainer
                key={boundary.id}
                legendMode={legendMode}
                boundary={boundary}
                boundaryTerritoryGroup={boundaryTerritoryGroup}
                boundaryPermissions={boundaryPermissions}
              />
            );
          }

          return null;
        })}

      {!displayBoundaryTerritories && (boundaryGroupArray.length > shownBoundaryKeySettingCountLimit) && (
        <BoundaryRowLoadMoreComponent
          onButtonClick={increaseShownBoundaryKeySettingCountLimit}
        />
      )}
    </ActiveBoundaryComponent>
  );
};
